/* eslint-disable react/no-multi-comp */
// @flow

import React, { useState, useEffect } from 'react';
import { log, numberWithCommas } from '../../utils/jsUtils';
import { useSelector, useDispatch } from 'react-redux';
import {Elements, CardElement, useStripe, useElements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import StripeElementWrapper from './StripeElementWrapper';

import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Radio from '@mui/material/Radio';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import SPBackend from '../../services/SPBackend';
import { auth } from '../../services/firebase';
import { setAccountUsage } from '../../reduxStore/modules/user/actions';

import { useTheme } from '@mui/material/styles';
import { createClasses } from '../../utils/stylesUtils';
import rechargeAccountDialogStyles from './Styles/RechargeAccountDialog.Style';
import signupStyles from '../PublicCpts/Styles/Signup.Style';
import type { Plan } from '../../services/SPBackend';

// Redux selectors - user state is flat, not nested
const selectUser = (state) => state.user;
const selectUserFullName = (state) => state.user?.fullName;
const selectDefaultUnit = (state) => state.user?.defaultUnit;

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const RechargesPicker = (props) => {
  const { selectPlan } = props;
  const dispatch = useDispatch();
  const [topupPlans, setTopupPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const classes = createClasses({
    ...rechargeAccountDialogStyles,
    // ...signupStyles
  }, theme);

  useEffect(() => {
    const firebaseUser = auth.currentUser;

    // Guard clause: only proceed if Firebase user is available
    if (!firebaseUser) {
      log('[RechargesPicker] No Firebase user available, skipping API call');
      return;
    }

    log('[RechargesPicker] Calling getMembersTopupPlans with firebaseUser');
    SPBackend.getMembersTopupPlans(firebaseUser)
    .then(response => {
      log('[getMembersTopupPlans] response: ', response.data);
      const { plans } = response.data;
      setTopupPlans(plans);
    })
    .catch(err => {
      log('[getMembersTopupPlans] error: ', err);
      setLoading(false);
      dispatch({
        type: 'ACTION_SET_SNACK_MSG',
        snackMessage: {
          message: err.message,
          level: 'error',
          duration: 5000
        }
      });
    });
  }, [dispatch]);

  return (
      <Grid sx={classes.contentCtr}
        item
        xs={12}
      >
        <Grid
          sx={classes.rechargesCtr}
          container
          direction='row'
          spacing={4}
        >
          <Grid sx={classes.rechargesTitleCtr}
            item
            xs={12}
          >
            <Typography sx={classes.h2}
              variant='h2'
            >Recharges</Typography>
            <Typography variant='body1'>Just recharge at least once every 12 months to keep your account active</Typography>
          </Grid>
          {
            topupPlans && topupPlans.map((topup: Plan, topupIdx) => (
            <Grid sx={classes.creditItemCtr}
                  item
                  key={`recharge-${topupIdx}`}
                  xs={12}
            >
              <Box sx={classes.itemBodyCtr}>
                <Box sx={classes.itemBodyLeft}>
                  <Typography sx={classes.topupCredits}
                              variant='body1'
                  >
                    {numberWithCommas(topup.UsageLimit)} Credits
                  </Typography>
                  <Typography sx={classes.topupCaption}
                              variant='body1'
                  >
                    ${(topup.Price * 1000 / topup.UsageLimit) / 1000} per Credit
                  </Typography>
                </Box>
                <Box sx={classes.itemBodyRight}
                  onClick={() => selectPlan(topup)}
                >
                  <Typography sx={{...classes.topupPrice, ...classes.clickable}}
                              variant='body1'
                  >
                    ${numberWithCommas(topup.Price)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={classes.itemFooter}>&nbsp;</Box>
            </Grid>
            ))
          }


          <Grid
            sx={{
              ...classes.newCenteredSectionCtr,
              ...classes.topMarginSpacing4,
              ...classes.bottomMarginSpacing2
            }}
            item
            xs={12}
          >
            <Grid container
              spacing={1}
            >
              <Grid item
                md={4}
                sm={12}
                xs={12}
              >
                <Typography sx={classes.h5}
                  variant='h5'
                >
                  No hidden costs
                </Typography>
                <Typography sx={classes.smallBody1}
                  variant='body1'
                >
                  That's right! All prices include taxes and fees.
                </Typography>
              </Grid>
              <Grid item
                md={4}
                sm={12}
                xs={12}
              >
                <Typography sx={classes.h5}
                  variant='h5'
                >
                  Simple pricing
                </Typography>
                <Typography sx={classes.smallBody1}
                  variant='body1'
                >
                  1 credit = 1 SMS up to 80 characters. <br />5 credits = 1
                  image / MMS.
                </Typography>
              </Grid>
              <Grid item
                md={4}
                sm={12}
                xs={12}
              >
                <Typography sx={classes.h5}
                  variant='h5'
                >
                  Free undelivered
                </Typography>
                <Typography sx={classes.smallBody1}
                  variant='body1'
                >
                  Only pay for your delivered messages.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
      </Grid>
    </Grid>
  );
};




const CkForm = (props) => {
  const {
    isLoading,
    selectedPlan,
    setOpenDialog,
    coupon,
    paymentMethods,
    defaultPaymentMethod,
    showAddNewCardForm,
    checkoutFlow,
    setCheckoutFlow
  } = props;

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [chargeError, setChargeError] = useState('');
  const [selectedPMId, setSelectedPMId] = useState('');
  
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const userFullName = useSelector(selectUserFullName);
  const defaultUnit = useSelector(selectDefaultUnit);

  const theme = useTheme();
  const classes = createClasses({
    ...rechargeAccountDialogStyles,
    ...signupStyles
  }, theme);

  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async(ev) => {
    try {
      ev.preventDefault();
      setPaymentLoading(true);
      log('submit payment!', ev);

      log('[handleSubmit] selectedPlan: ', selectedPlan);
      if (!selectedPlan || typeof selectedPlan === 'undefined') {
        setChargeError('Please choose a plan first.');
        throw Error('Please choose a plan first.');
      }
      
      let selectedPM = null;
      if (selectedPMId !== '') {
        selectedPM = {
          id: selectedPMId
        };
      } else {

        // CREATE NEW PM

        // https://stripe.com/docs/stripe-js/reference#elements-get-element
        const cardElement = elements.getElement('card');
        // cardElement.mount('#card-elt-id');
        log('[handleSubmit] elements(cardNumber): ', cardElement);
        let pmResult = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: cardholderName
          }
        });
        log('[handleSubmit] after createPaymentMethod. ');
        const { paymentMethod, error: pmError } = pmResult;
        if (pmError) {
          log('Stripe paymentMethod error: ', pmError);
          if (pmError.code === 'parameter_invalid_empty') {
            throw Error('All fields are required.');
          }
          throw Error(pmError.message);
        }
        const firebaseUser = auth.currentUser;
        try {
          const pmResponse = await SPBackend.updatePaymentMethod(paymentMethod, firebaseUser);
          log('[handleSubmit] pmResponse: ', pmResponse);
        } catch (error) {
            log('[handleSubmit] charge error.response: ', error.response);
            setPaymentLoading(false);
            const data = error.response?.data || 'Account recharge failed. Please try again.';
            setChargeError(data);
        }

        log('resulting pmResult: ', paymentMethod);
        selectedPM = paymentMethod;
      }

      // USE SELECTED PM

      const firebaseUser = auth.currentUser;

      // Omit trialEnd param, so trial expires now.
      const paymentPayload = {
        paymentMethod: selectedPM,
        planId: selectedPlan._id
      };
      if (typeof coupon !== 'undefined' && coupon !== null) {
        paymentPayload.coupon = coupon.id;
      }
      SPBackend.purchaseTopupPlan(firebaseUser, selectedPlan._id, paymentPayload)
        .then((response) => {
          setChargeError('');
          log('[purchaseTopupPlan] response: ', response);
          SPBackend.getAccount(firebaseUser)
          .then(response => {
            log('[getAccount] response: ', response);
            const { data } = response;
            const { account, billing } = data;
            dispatch({
              type: 'ACTION_LOADED_ACCOUNT',
              payload: {
                billing
              }
            });
            dispatch({
              type: 'ACTION_SET_ACCOUNT_SETTINGS', 
              accountSettings: account.Settings
            });
            dispatch(setAccountUsage(account.Usage));
            dispatch({
              type: 'ACTION_SET_BILLING',
              billing
            });
            setPaymentLoading(false);
            setCheckoutFlow((prev) => ({
              ...prev,
              currentStep: 2
            }));
            log('[chargePlan] updated checkoutflow');
          });
        })
        .catch(error => {
          log('[purchaseTopupPlan] charge error.response: ', error.response);
          setPaymentLoading(false);
          const data = error.response?.data || 'Topup purchase failed. Please try again.';
          setChargeError(data);
        });
    } catch (e) {
      log('[handleSubmit] e.name: ', e.name);
      if (e.name === 'IntegrationError') {
        setChargeError('Missing card information');
      } else {
        log('[handleSubmit] error caught: ', e.name, e);
        setChargeError(e.message);
      }
      setPaymentLoading(false);
    }
  };

  log('[CkForm] selectedPlan: ', selectedPlan);

  useEffect(() => {
    if (defaultPaymentMethod !== null) {
      setSelectedPMId(defaultPaymentMethod.id);
    }
  }, [defaultPaymentMethod]);

  const handleSelectPaymentMethod = (pmId) => {
    log('[handleSelectPaymentMethod] pmId: ', pmId);
    setSelectedPMId(pmId);
  };

  return (
    <Grid container>
      <Box component="form"
           sx={classes.pmFormCtr}
           onSubmit={handleSubmit}
      >
        <Typography sx={{...classes.spaceTop, ...classes.spaceTop}}
                    variant='body1'
        ><b>Payment method</b></Typography>
        {
          isLoading
          ? <Typography variant='body1'>Loading...</Typography>
          : null
        }
      {
        !showAddNewCardForm
        ? <List>
            <RadioGroup aria-label="gender"
              sx={classes.pmRadioGroup}
              color='primary'
              name="selectPaymentMethod"
              onChange={(e) => handleSelectPaymentMethod(e.target.value)}
              value={selectedPMId}
            >
      
              {
                paymentMethods.map((pm, i) => {
                  return (
                    <ListItem sx={classes.pmListItem}
                      key={`pm-${i}`}
                    >
                      <ListItemAvatar>
                        <FormControlLabel
                          control={<Radio color='primary'/>}
                          // onChange={handleSelectPaymentMethod}
                          value={pm.id}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        onClick={() => handleSelectPaymentMethod(pm.id)}
                        primary={`${pm.card.brand.toUpperCase()} (${pm.card.last4}) - Expires ${pm.card.exp_month}/${pm.card.exp_year}`}
                        secondary={defaultPaymentMethod && pm.id === defaultPaymentMethod.id ?'(Default)':''}
                      />

                    {/* <div key={`pm-${i}`}>
                      <span className={classes.capitalize}>{pm.card.brand}</span> ({pm.card.last4}) - Expires {pm.card.exp_month}/{pm.card.exp_year}&nbsp;&nbsp;
                    {defaultPaymentMethod && pm.id === defaultPaymentMethod.id ? <Chip classes={{root: classes.defaultChip}}
                      label='default' */}
                    </ListItem>
                  );
                })
              }
            </RadioGroup>
          </List>
        : null
      }
      {
        showAddNewCardForm
        ? <><Grid item
            xs={12}
            >
              <Typography sx={classes.formLabel}
                          variant='body1'
              >{'Cardholder full name'}</Typography>
              <FormControl
                sx={classes.newInputForm}
                variant="outlined"
              >
                <OutlinedInput
                  sx={{...classes.newFormElt, ...classes.checkoutInputForm}}
                  id='companyName'
                  margin='dense'
                  name='companyName'
                  onChange={event => setCardholderName(event.target.value)}
                  placeholder='Cardholder full name'
                  type='text'
                  value={cardholderName}
                />
              </FormControl>
            </Grid>
            <Grid item
              xs={12}
            >
              <Typography sx={classes.formLabel}
                          variant='body1'
              >{'Card information'}</Typography>
              <StripeElementWrapper component={CardElement}
                                    id='card-elt-id'
                                    label=''
              />
            </Grid> 
          </>
        : null
      }
      {
        chargeError !== ''
        ? <Grid
            sx={classes.errorLabel}
            item
            xs={12}
          >{chargeError}</Grid>
        : null
      }

        <Box sx={classes.reviewItemCtr}>
          <Typography variant='body1'><b>Payment total</b></Typography>
          <Typography variant='body1'><b>${numberWithCommas(checkoutFlow.planDef.Price)}</b></Typography>
        </Box> 
        <Button
          color='primary'
          disabled={paymentLoading || (!showAddNewCardForm && (!selectedPlan || selectedPlan._id === ''))}
          style={{marginTop: 16}}
          type='submit'
          variant='contained'
        >{selectedPlan && selectedPlan._id ? 'Purchase' : 'Loading'}</Button>
      </Box>
    </Grid>
  );
};

const CheckoutForm = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <CkForm {...props} />
    </Elements>
  );
};

const CheckoutPlan = (props) => {
  // console.log('[CheckoutPlan] props: ', props);
  const {
    setCheckoutFlow,
    checkoutFlow,
    setOpenDialog
  } = props;

  const dispatch = useDispatch();

  const theme = useTheme();
  const classes = createClasses({
    ...rechargeAccountDialogStyles,
    ...signupStyles
  }, theme);

  const emptyPlanState = {
    Price: 0,
    Privacy: '',
    StripePlanId: '',
    TrialLimit: 0,
    Type: 'topup',
    UsageLimit: 0,
    _id: ''
  };
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [showAddNewCardForm, setShowAddNewCardForm] = useState(false);
  const [planInfo, setPlanInfo] = useState(emptyPlanState);
  const [isLoadingPM, setIsLoadingPM] = useState(false);

  useEffect(() => {
    log('[useEffect.checkoutFlow] planInfo: ', planInfo);
    setPlanInfo(checkoutFlow.planDef);
    setIsLoadingPM(true);

    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      log('[CheckoutPlan] No Firebase user available');
      setIsLoadingPM(false);
      return;
    }

    // Check if user already has a payment method
    SPBackend.getPaymentMethods(firebaseUser)
      .then(response => {
        log('[getPaymentMethods] response: ', response);
        const {
          paymentMethods,
          defaultPaymentMethod
        } = response.data;
        setPaymentMethods(paymentMethods);
        setDefaultPaymentMethod(defaultPaymentMethod);
        if (typeof paymentMethods !== 'undefined' && paymentMethods !== null && paymentMethods.length > 0) {
          setShowAddNewCardForm(false);
        } else {
          setShowAddNewCardForm(true);
        }
        setIsLoadingPM(false);
      })
      .catch(e => {
        log('[getPaymentMethods] error: ', e);
        setShowAddNewCardForm(false);
        setIsLoadingPM(false);
      });
  }, [checkoutFlow]);

  return (
    <Box sx={classes.checkoutColCtr}>
      <Box sx={classes.checkoutContentHeader}>
        <Typography variant='h3'>Finalize & confirm</Typography>
      </Box>
      <Box sx={classes.checkoutLineCtr}>
        <Box sx={classes.checkoutColCtr}>
          <Box sx={classes.reviewSectionCtr}>
            <Typography sx={classes.spaceTop}
              variant='body1'
            ><b>Review details</b></Typography>
            <Box sx={{...classes.reviewItemCtr, ...classes.reviewItem, ...classes.pmListItem}}>
              <Typography variant='body1'>{numberWithCommas(checkoutFlow.planDef.UsageLimit)} credits recharge</Typography>
              <Typography sx={{...classes.bottomText, ...classes.price}}
                          variant='body1'
              >${numberWithCommas(checkoutFlow.planDef.Price)}</Typography>
            </Box>
            <CheckoutForm
              // coupon={coupon} // FIXME
              // history={history}
              checkoutFlow={checkoutFlow}
              defaultPaymentMethod={defaultPaymentMethod}
              isLoading={isLoadingPM}
              paymentMethods={paymentMethods}
              selectedPlan={checkoutFlow.planDef}
              setCheckoutFlow={setCheckoutFlow}
              setOpenDialog={setOpenDialog}
              showAddNewCardForm={showAddNewCardForm}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

type Props = {
  setOpen: Function,
  open: boolean,
  triggerAction: Function
};

// eslint-disable-next-line react/no-multi-comp
const RechargeAccountDialog = (props: Props) => {
  const emptyCheckoutFlowState = {
    currentStep: 0, // 0: pick a plan; 1: payment; 2: success!
    selectedPlanId: null
  };
  const {
    setOpen,
    open,
    triggerAction
  } = props;
  
  const user = useSelector(selectUser);
  const theme = useTheme();
  const classes = createClasses({
    ...rechargeAccountDialogStyles,
    ...signupStyles
  }, theme);
  
  const [checkoutFlow, setCheckoutFlow] = useState(emptyCheckoutFlowState);

  const handleSelectPlan = (topupPlan) => {
    log('[handleSelectPlan] topupPlan: ', topupPlan);
    setCheckoutFlow((prev) => {
      return {
        ...prev,
        currentStep: prev.currentStep + 1,
        selectedPlanId: topupPlan.StripePlanId, // stripe plan id
        planDef: topupPlan
      };
    });
  };

  const emptyCheckoutFlow = () => {
    setTimeout(() => {
      setCheckoutFlow(emptyCheckoutFlowState);
    }, 300);
  };
  

  // Add debug logging to understand the render state
  log('[RechargeAccountDialog] render - open:', open, 'user:', user, 'checkoutFlow:', checkoutFlow);
  
  return (
    <Dialog aria-labelledby='form-confirm-topup'
      PaperProps={{sx: classes.dialogCtr}}
      onClose={() => {setOpen(false); emptyCheckoutFlow();}}
      open={open || false}
    >
      <DialogContent sx={{...classes.grow, ...classes.flexContainer}}>
      {
        checkoutFlow.currentStep === 0
        ? <RechargesPicker
            selectPlan={handleSelectPlan}
          />
        : null
      }
      {
        checkoutFlow.currentStep === 1
        ? <CheckoutPlan
          checkoutFlow={checkoutFlow}
          setCheckoutFlow={setCheckoutFlow}
          setOpenDialog={setOpen}
          />
        : null
      }
      {
        checkoutFlow.currentStep === 2
        ? <Grid sx={classes.contentCtr}
            item
            xs={12}
          >
            <Box sx={classes.checkoutColCtr}>
              <Box sx={classes.checkoutContentHeader}>
                <Typography variant='h3'>Success! You're ready to text 🤗</Typography>
              </Box>
              <Box sx={classes.checkoutColCtr}>
                <Typography sx={classes.captionText}
                  variant='body1'
                >Your invoice is available in the Billing section.</Typography>
                <Box sx={classes.spaceTop} />
                <Button
                  color='primary'
                  onClick={() => {setOpen(false); emptyCheckoutFlow();}}
                  style={{marginTop: 16}}
                  variant='contained'
                >Got it</Button>
                <Box sx={classes.spaceTop} />
              </Box>
            </Box>
          </Grid>
        : null
      }
      
      </DialogContent>
    </Dialog>
  );
};

export default RechargeAccountDialog;
